import type { ApiResult } from '../game/http-types.js';

/** A rejected API call; `code` is the server's error code so callers can tell a stale revision from a real refusal. */
export class ApiError extends Error { constructor(readonly code: string, message: string) { super(message); } }

export async function callApi<T>(url: string, body?: object): Promise<T> {
  const response = await fetch(url, body === undefined ? { credentials: 'same-origin' } : { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json() as ApiResult<T>;
  if (!result.ok) throw new ApiError(result.error.code, result.error.message);
  return result.value;
}

/**
 * The server raises a character's revision on its own (kill experience, mastery, a death), so a request built from the
 * last `/api/me` can be refused as STALE_REVISION through no fault of the player. Ask for the current revision and send
 * the request once more; a second refusal is reported as is. The server keeps its check, which still stops a double click.
 */
export async function withStaleRetry<T>(send: (revision: number) => Promise<T>, revision: number, currentRevision: () => Promise<number>): Promise<T> {
  try { return await send(revision); }
  catch (error) {
    if (!(error instanceof ApiError) || error.code !== 'STALE_REVISION') throw error;
    return send(await currentRevision());
  }
}
